import useCareCaptureDate from '../../hooks/useCareCaptureDate';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  AddRounded as AddIcon,
  NotificationsNoneRounded as NotificationsIcon,
  SearchRounded as SearchIcon,
} from '@mui/icons-material';
import { useDashboardView } from './shared/DashboardViewContext';
import { getCalendarDateKey } from '../../utils/calendarDateKey';
import TimelineHeaderControls from '../Timeline/TimelineHeaderControls';
import UnifiedTimeline from '../Timeline/UnifiedTimeline';
import colors from '../../assets/theme/colors';
import SectionButton from './shared/SectionButton';
import DashboardQuickEntryCTA from './shared/DashboardQuickEntryCTA';
import { DASHBOARD_QUICK_ENTRY_COPY } from './shared/dashboardQuickEntryContent';
import { DASHBOARD_ACTION_IDS } from './shared/dashboardActionDefinitions';
import { ACTIVE_TIMELINE_DATE_STORAGE_KEY } from './shared/DashboardViewContext';
import { collectTeamMembers, getTeamRolePresentation } from '../CareTeam/careTeamPresentation';
import { getTimelineEntryDate } from '../../services/timeline/dateUtils';
import { getChildPhotoSource } from '../../utils/childPhoto';
import { PRODUCT_NAME } from '../../constants/config';
import { canEditChildProfile } from '../../constants/roles';
import { getTimelineMetaForCategory } from '../../constants/logTypeRegistry';
import StructuredTrackingLauncher from './shared/StructuredTrackingLauncher';
import AskRecordLauncher from '../AskRecord/AskRecordLauncher';
import useAskRecordSourceNavigation from '../AskRecord/useAskRecordSourceNavigation';
import { requestTimelineEntryFocus } from '../AskRecord/askRecordSourceNavigation';
import { getChildProfileSetupStatus } from '../../utils/profileCompletion';
import { ProfileSetupAvatar, ProfileSetupPill } from './shared/ProfileSetupIndicator';
import { getChildAccentTheme } from './shared/childAccentTheme';
import { CareSourceStatusPanel, CARE_SOURCE_CONTEXTS } from '../../features/careSourceProcessing';
import { formatTimelineTime } from '../../hooks/timeline/timelineDateUtils';
import { requestProfileInboundEmailOpen } from '../../features/inboundEmail/profileInboundEmailOpenRequest';

const formatStreakLabel = (streak = 0) => {
  if (!streak || streak < 1) {
    return '';
  }

  return `${streak}-day streak`;
};

const getEntryDate = (entry) => {
  if (!entry) {
    return null;
  }

  const candidate = getTimelineEntryDate(entry)
    || entry.updatedAt?.toDate?.()
    || entry.updatedAt
    || (typeof entry.timestamp?.toDate === 'function' ? entry.timestamp.toDate() : null)
    || new Date(entry.timestamp);

  return candidate instanceof Date && !Number.isNaN(candidate.getTime()) ? candidate : null;
};

const getEntryCategory = (entry = {}) => (
  entry.category
  || entry.type
  || entry.timelineType
  || entry.actionType
  || entry.logCategory
  || 'log'
);

const getEntryText = (entry = {}) => String(
  entry.content
  || entry.text
  || entry.notes
  || entry.description
  || entry.summary
  || entry.title
  || entry.label
  || 'Logged update'
).trim();

const getEntryTimeLabel = (entry) => {
  return formatTimelineTime(entry) || '';
};

const isSameCalendarDate = (dateA, dateB) => (
  getCalendarDateKey(dateA) === getCalendarDateKey(dateB)
);

const DesktopDashboardWorkspace = ({
  hook,
  onQuickEntry,
  onComposerSubmit,
  composerSaving = false,
  composerSavingLabel = undefined,
  composerSavingHelper = undefined,
  onImportLogs,
  loggingRemindersByChild = {},
  loggingReminderLoadingByChild = {},
  onToggleLoggingReminder,
  timelineNavigationState = null,
}) => {
  const navigate = useNavigate();
  const { activeChildId, setActiveChildId } = useDashboardView();
  const initialTimelineDate = timelineNavigationState?.selectedDate
    ? new Date(timelineNavigationState.selectedDate)
    : timelineNavigationState?.originDate
      ? new Date(timelineNavigationState.originDate)
      : null;
  const [selectedDate, setSelectedDate] = useState(() => (
    initialTimelineDate instanceof Date && !Number.isNaN(initialTimelineDate.getTime())
      ? initialTimelineDate
      : new Date()
  ));
  const [searchQuery, setSearchQuery] = useState('');
  const [timelineFocusEntryId, setTimelineFocusEntryId] = useState(() => timelineNavigationState?.timelineEntryId || timelineNavigationState?.originEntryId || null);
  const hasAppliedTimelineNavigationRef = useRef(false);
  const timelineRef = useRef(null);
  const mainRef = useRef(null);
  const rightRailRef = useRef(null);
  const activeChild = useMemo(
    () => hook.children.find((child) => child.id === activeChildId) || hook.children[0] || null,
    [activeChildId, hook.children]
  );
  const {captureDate,setCaptureDate,setCaptureDateWasSelected,handleCaptureDateChange} = useCareCaptureDate(activeChild?.id);
  const activeChildAccent = useMemo(
    () => getChildAccentTheme(activeChild || {}),
    [activeChild]
  );
  const activeChildSummary = useMemo(
    () => hook.timelineSummary?.[activeChild?.id] || hook.timelineSummary || {},
    [activeChild?.id, hook.timelineSummary]
  );
  const activeChildEntries = useMemo(
    () => hook.allEntries?.[activeChild?.id] || [],
    [activeChild?.id, hook.allEntries]
  );
  const activeChildCareTeam = useMemo(
    () => collectTeamMembers(activeChild || {}),
    [activeChild]
  );
  const activeProfileSetupStatus = useMemo(
    () => getChildProfileSetupStatus(activeChild || {}, {
      careTeamMembers: activeChildCareTeam,
    }),
    [activeChild, activeChildCareTeam]
  );
  const activeChildRole = hook.getUserRoleForChild?.(activeChild?.id) || null;
  const canEditActiveChildProfile = canEditChildProfile(activeChildRole);
  const selectedDateEntries = useMemo(() => (
    activeChildEntries
      .filter((entry) => entry?.status !== 'deleted')
      .filter((entry) => {
        const entryDate = getEntryDate(entry);
        return entryDate && isSameCalendarDate(entryDate, selectedDate);
      })
      .sort((a, b) => getEntryDate(b) - getEntryDate(a))
  ), [activeChildEntries, selectedDate]);
  const visibleRightRailEntries = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const entries = normalizedQuery
      ? selectedDateEntries.filter((entry) => (
          getEntryText(entry).toLowerCase().includes(normalizedQuery)
          || String(getEntryCategory(entry)).toLowerCase().includes(normalizedQuery)
        ))
      : selectedDateEntries;

    return entries.slice(0, 5);
  }, [searchQuery, selectedDateEntries]);
  const activityStreakLabel = formatStreakLabel(activeChildSummary.activityStreak || 0);

  useEffect(() => {
    if ((timelineNavigationState?.originDate || timelineNavigationState?.selectedDate || timelineNavigationState?.timelineEntryId || timelineNavigationState?.originEntryId) && !hasAppliedTimelineNavigationRef.current) {
      hasAppliedTimelineNavigationRef.current = true;
      setCaptureDate(new Date());
      setCaptureDateWasSelected(false);
      setSearchQuery('');
      return;
    }

    setSelectedDate(new Date());
    setCaptureDate(new Date());
    setCaptureDateWasSelected(false);
    setSearchQuery('');
    setTimelineFocusEntryId(null);
  }, [activeChild?.id, timelineNavigationState]);


  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const selectedDateKey = getCalendarDateKey(selectedDate);
    if (selectedDateKey) {
      window.localStorage.setItem(ACTIVE_TIMELINE_DATE_STORAGE_KEY, selectedDateKey);
      window.dispatchEvent(new CustomEvent('captureez:timeline-date-changed', {
        detail: { dateKey: selectedDateKey },
      }));
    }
  }, [selectedDate]);

  const handleDesktopAction = (action) => {
    const child = activeChild;
    switch (action) {
      case DASHBOARD_ACTION_IDS.ADD_CHILD:
        hook.handleAddChild?.();
        break;
      case DASHBOARD_ACTION_IDS.INVITE_CAREGIVER:
        if (child) {
          hook.handleInviteTeamMember?.(child.id);
        }
        break;
      case DASHBOARD_ACTION_IDS.PREP_FOR_THERAPY:
        if (child) {
          hook.handleShowCareReport?.(child);
        }
        break;
      case DASHBOARD_ACTION_IDS.IMPORT_LOGS:
        if (child) {
          onImportLogs?.(child);
        }
        break;
      case DASHBOARD_ACTION_IDS.EDIT_CHILD:
        if (child) {
          hook.handleEditChild?.(child);
        }
        break;
      default:
        break;
    }
  };

  const handleOpenPatternsPage = (child) => {
    if (!child?.id) {
      return;
    }

    navigate('/patterns', { state: { selectedChildId: child.id } });
  };

  const handleOpenCareKnowledge = (child) => {
    if (!child?.id) {
      return;
    }

    navigate('/care-knowledge', { state: { childId: child.id } });
  };

  const handleOpenIepJourney = (child) => {
    if (child?.id) navigate(`/children/${child.id}/journeys/iep-meeting`);
  };

  const handleOpenCustomPrepare = (child) => {
    if (child?.id) navigate('/prepare', { state: { selectedChildId: child.id } });
  };

  const handleOpenMoodSheet = () => {
    if (!activeChild) {
      return;
    }

    hook.setMoodSheetChild?.(activeChild);
    hook.setMoodSheetEntry?.(null);
    hook.setShowMoodSheet?.(true);
  };

  const handleOpenResponseSheet = () => {
    if (!activeChild) {
      return;
    }

    hook.handleQuickDataEntry?.(activeChild, 'incident');
  };

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const handleOpenAskSource = useAskRecordSourceNavigation({
    beforeNavigate: () => setSearchQuery(''),
    setSelectedDate,
    setTimelineFocusEntryId,
    timelineRef,
  });

  const handleReviewSourceTimeline = useCallback((target = {}) => {
    if (target?.date) {
      setSelectedDate(target.date);
    }

    setSearchQuery('');
    requestTimelineEntryFocus(setTimelineFocusEntryId, target?.entryId);
    window.setTimeout(() => {
      timelineRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [setSelectedDate]);

  if (!activeChild) {
    return null;
  }

  return (
    <Box
      sx={{
        bgcolor: colors.semantic.bg,
        minHeight: '100%',
        color: colors.semantic.ink,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderBottom: `1px solid ${alpha(colors.brand.tint, 0.16)}`,
          bgcolor: alpha('#FFFFFF', 0.96),
          backdropFilter: 'blur(16px)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 1760,
            mx: 'auto',
            px: { md: 1.5, lg: 2.1, xl: 2.6 },
            py: 0.75,
            display: 'grid',
            gridTemplateColumns: 'auto minmax(260px, 1fr) minmax(300px, 460px) auto',
            alignItems: 'center',
            gap: 1.1,
          }}
        >
          <Typography
            sx={{
              fontSize: '1.28rem',
              fontWeight: 900,
              lineHeight: 1,
              color: colors.semantic.ink,
              whiteSpace: 'nowrap',
            }}
          >
            {PRODUCT_NAME}.
          </Typography>

          <Box
            sx={{
              minWidth: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.35,
              justifySelf: 'start',
              maxWidth: '100%',
              p: 0.32,
              borderRadius: '999px',
              bgcolor: alpha(colors.brand.tint, 0.1),
              overflowX: 'auto',
            }}
          >
            {hook.children.map((child) => {
              const selected = child.id === activeChild.id;
              const childAccent = getChildAccentTheme(child, { children: hook.children });
              const childCareTeamMembers = collectTeamMembers(child);
              const setupStatus = getChildProfileSetupStatus(child, {
                careTeamMembers: childCareTeamMembers,
              });
              const childRole = hook.getUserRoleForChild?.(child.id) || null;
              const canEditProfile = canEditChildProfile(childRole);
              return (
                <ButtonBase
                  key={child.id}
                  onClick={() => {
                    if (selected && canEditProfile && typeof hook.handleEditChild === 'function') {
                      hook.handleEditChild(child);
                      return;
                    }

                    if (selected && !canEditProfile && typeof hook.handleReviewChild === 'function') {
                      hook.handleReviewChild(child);
                      return;
                    }

                    setActiveChildId(child.id);
                  }}
                  aria-label={selected
                    ? `${canEditProfile ? 'Open' : 'View'} ${child.name || 'profile'} ${canEditProfile ? 'setup' : 'care story'}`
                    : `Switch to ${child.name || 'profile'}`}
                  sx={{
                    flex: '0 0 auto',
                    minHeight: 38,
                    px: selected ? 0.45 : 0.4,
                    pr: selected ? 0.95 : 0.75,
                    borderRadius: '999px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: selected ? '#FFFFFF' : 'transparent',
                    boxShadow: selected ? `0 6px 16px ${alpha(childAccent.primary, 0.18)}` : 'none',
                    color: selected ? colors.semantic.ink : alpha(colors.semantic.ink, 0.72),
                  }}
                >
                  <ProfileSetupAvatar
                    child={child}
                    setupStatus={setupStatus}
                    size={26}
                    ringWidth={3}
                    mode={canEditProfile ? 'edit' : 'view'}
                    accentColor={childAccent.primary}
                    showPercentBadge={selected && canEditProfile}
                    showAttentionDot={selected && canEditProfile}
                    avatarSx={{
                      bgcolor: getChildPhotoSource(child) ? '#EAF2EE' : childAccent.soft,
                      color: childAccent.ink,
                      boxShadow: 'none',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: selected ? 900 : 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                    {child.name || 'Profile'}
                  </Typography>
                </ButtonBase>
              );
            })}
            <ButtonBase
              onClick={() => hook.handleAddChild?.()}
              aria-label="Add child"
              sx={{
                flex: '0 0 auto',
                width: 34,
                height: 34,
                borderRadius: '999px',
                display: 'grid',
                placeItems: 'center',
                color: colors.brand.deep,
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </ButtonBase>
            {canEditActiveChildProfile ? (
              <ProfileSetupPill
                setupStatus={activeProfileSetupStatus}
                onClick={() => hook.handleEditChild?.(activeChild)}
                compact
                sx={{
                  ml: 0.2,
                  maxWidth: 220,
                }}
              />
            ) : null}
          </Box>

          <Box
            sx={{
              minHeight: 40,
              px: 0.95,
              display: 'grid',
              gridTemplateColumns: '22px minmax(0, 1fr) auto',
              alignItems: 'center',
              gap: 0.55,
              borderRadius: '14px',
              bgcolor: '#FFFFFF',
              border: `1px solid ${alpha(colors.brand.tint, 0.18)}`,
            }}
          >
            <SearchIcon sx={{ fontSize: 19, color: alpha(colors.semantic.ink, 0.52) }} />
            <Box
              component="input"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search logs..."
              aria-label="Search logs"
              sx={{
                width: '100%',
                border: 0,
                outline: 0,
                bgcolor: 'transparent',
                color: colors.semantic.ink,
                font: 'inherit',
                fontSize: '0.9rem',
                fontWeight: 600,
                minWidth: 0,
                '&::placeholder': {
                  color: alpha(colors.semantic.ink, 0.5),
                },
              }}
            />
            <Typography sx={{ px: 0.5, py: 0.2, borderRadius: '7px', bgcolor: alpha(colors.brand.tint, 0.08), color: alpha(colors.semantic.ink, 0.56), fontSize: '0.68rem', fontWeight: 850 }}>
              ⌘K
            </Typography>
          </Box>

          <ButtonBase
            aria-label="Notifications"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '999px',
              bgcolor: '#FFFFFF',
              border: `1px solid ${alpha(colors.brand.tint, 0.18)}`,
              color: alpha(colors.semantic.ink, 0.72),
            }}
          >
            <NotificationsIcon sx={{ fontSize: 20 }} />
          </ButtonBase>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          maxWidth: 1760,
          mx: 'auto',
          px: { md: 1.5, lg: 2.1, xl: 2.6 },
          py: { md: 1.4, xl: 1.8 },
          display: 'grid',
          gridTemplateColumns: {
            md: '190px minmax(0, 1fr) 300px',
            lg: '220px minmax(0, 1fr) 330px',
            xl: '240px minmax(0, 1fr) 360px',
          },
          gap: { md: 1.2, lg: 1.5, xl: 1.8 },
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 78,
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 0.75,
              borderRadius: '18px',
              bgcolor: '#FFFFFF',
              border: `1px solid ${alpha(colors.brand.tint, 0.16)}`,
              boxShadow: 'none',
            }}
          >
            <Stack spacing={0.25}>
              <SectionButton accent={activeChildAccent} onClick={() => navigate('/home')}>Home</SectionButton>
              <SectionButton accent={activeChildAccent} active>Timeline</SectionButton>
              <SectionButton accent={activeChildAccent} onClick={() => navigate('/care-profile', { state: { childId: activeChild?.id || null } })}>Care Profile</SectionButton>
              <SectionButton accent={activeChildAccent} onClick={() => scrollTo(rightRailRef)}>Insights</SectionButton>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 1.1,
              borderRadius: '18px',
              bgcolor: '#FFFFFF',
              border: `1px solid ${alpha(colors.brand.tint, 0.16)}`,
              boxShadow: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.58) }}>
                Care Circle
              </Typography>
              <ButtonBase
                onClick={() => hook.handleInviteTeamMember?.(activeChild.id)}
                aria-label="Invite care circle member"
                sx={{ width: 24, height: 24, borderRadius: '999px', color: colors.brand.deep }}
              >
                <AddIcon sx={{ fontSize: 17 }} />
              </ButtonBase>
            </Box>

            <Stack spacing={0.75}>
              {activeChildCareTeam.slice(0, 5).map((member) => (
                <Box key={member.uid || member.email || member.name} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.84rem', fontWeight: 850, color: colors.semantic.ink, lineHeight: 1.12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {member.displayName || member.name || 'Care circle member'}
                  </Typography>
                  <Typography sx={{ mt: 0.15, fontSize: '0.73rem', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getTeamRolePresentation(member.role, { signsOriginals: activeChild?.staffCaregivers?.[member.uid]?.enabled === true }).label}
                  </Typography>
                </Box>
              ))}
              {!activeChildCareTeam.length ? (
                <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.35, color: alpha(colors.semantic.ink, 0.62) }}>
                  Add family and care partners here.
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        </Box>

        <Box ref={mainRef} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <DashboardQuickEntryCTA
            child={activeChild}
            onQuickEntry={onQuickEntry}
            onComposerSubmit={onComposerSubmit}
            composerSaving={composerSaving}
            composerSavingLabel={composerSavingLabel}
            composerSavingHelper={composerSavingHelper}
            onImportLogs={onImportLogs}
            onForwardEmail={['care_owner', 'care_partner'].includes(activeChildRole)
              ? (child) => requestProfileInboundEmailOpen(child?.id)
              : null}
            onOpenSleepLog={hook.handleOpenSleepLog}
            onOpenFoodLog={hook.handleOpenFoodLog}
            onOpenMedicalLog={hook.handleOpenMedicalLog}
            onOpenBathroomLog={hook.handleOpenBathroomLog}
            onOpenIncident={handleOpenResponseSheet}
            captureDate={captureDate}
            onCaptureDateChange={handleCaptureDateChange}
            loggingReminderEnabled={Boolean(loggingRemindersByChild?.[activeChild?.id])}
            loggingReminderLoading={Boolean(loggingReminderLoadingByChild?.[activeChild?.id])}
            onToggleLoggingReminder={onToggleLoggingReminder}
            variant="composer"
            sx={{
              borderRadius: '20px',
              px: { md: 1.6, lg: 1.9 },
              py: { md: 1.55, lg: 1.85 },
            }}
          />
          <CareSourceStatusPanel
            childId={activeChild?.id || ''}
            sourceContext={CARE_SOURCE_CONTEXTS.TIMELINE}
            onReviewTimeline={handleReviewSourceTimeline}
            onReviewAppointment={(appointmentDraft) => navigate(
              `/children/${activeChild.id}/appointments`,
              appointmentDraft?.id ? undefined : { state: { appointmentDraft } },
            )}
            canManageAppointments={['care_owner', 'care_partner'].includes(activeChildRole)}
            appointmentTimeZone={activeChild.timeZone || activeChild.timezone || ''}
            initialOpenCaptureDetailsKey={timelineNavigationState?.openCaptureDetailsKey || ''}
            initialOpenCaptureRequestKey={timelineNavigationState?.openCaptureDetailsRequestKey || ''}
          />

          <StructuredTrackingLauncher
            child={activeChild}
            variant="section"
            askLauncher={(
              <AskRecordLauncher
                child={activeChild}
                logCount={activeChildEntries.length}
                onOpenSource={handleOpenAskSource}
                variant="toolsHero"
              />
            )}
            onOpenCareKnowledge={handleOpenCareKnowledge}
            onOpenInsights={handleOpenPatternsPage}
            onOpenTherapyPrep={hook.handleShowCareReport}
            onOpenCustomPrepare={handleOpenCustomPrepare}
            onOpenIepJourney={handleOpenIepJourney}
            onOpenMood={handleOpenMoodSheet}
            onOpenCustomNote={onQuickEntry}
            onImportLogs={onImportLogs}
          />

          <Paper
            ref={timelineRef}
            elevation={0}
            sx={{
              p: 1.2,
              borderRadius: '18px',
              bgcolor: '#FFFFFF',
              border: `1px solid ${alpha(colors.brand.tint, 0.16)}`,
              boxShadow: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.95 }}>
              <Box>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, color: colors.semantic.ink, lineHeight: 1.1 }}>
                  Today
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: '0.76rem', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.2 }}>
                  {activityStreakLabel || `${selectedDateEntries.length} logs`}
                </Typography>
              </Box>
            </Box>

            <TimelineHeaderControls
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              calendarEntries={activeChildEntries}
              mobileLayout={false}
              sx={{ mb: 0.85 }}
            />

            <UnifiedTimeline
              child={activeChild}
              selectedDate={selectedDate}
              showDaySummary
              streakLabel={activityStreakLabel}
              calendarEntries={activeChildEntries}
              focusEntryId={timelineFocusEntryId}
              showSourceActivity={false}
              onReviewAppointment={(appointmentDraft) => navigate(
                `/children/${activeChild.id}/appointments`,
                { state: { appointmentDraft } },
              )}
            />
          </Paper>
        </Box>

        <Box
          ref={rightRailRef}
          sx={{
            minWidth: 0,
            position: 'sticky',
            top: 78,
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.1,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 1.05,
              borderRadius: '18px',
              bgcolor: '#FFFFFF',
              border: `1px solid ${alpha(colors.brand.tint, 0.16)}`,
              boxShadow: 'none',
            }}
          >
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.62), mb: 0.85 }}>
              Today&apos;s logs
            </Typography>

            <Stack spacing={0.65}>
              {visibleRightRailEntries.map((entry) => {
                const category = getEntryCategory(entry);
                const meta = getTimelineMetaForCategory(category, { importantMoment: Boolean(entry.importantMoment) });
                const paletteColor = meta.color || colors.brand.deep;
                const Icon = meta.iconComponent;
                return (
                  <Box
                    key={entry.id || `${getEntryText(entry)}-${getEntryTimeLabel(entry)}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '34px minmax(0, 1fr) auto',
                      gap: 0.75,
                      alignItems: 'start',
                      p: 0.78,
                      borderRadius: '14px',
                      border: `1px solid ${alpha(colors.brand.tint, 0.14)}`,
                      bgcolor: '#FFFFFF',
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '999px',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: alpha(paletteColor, 0.16),
                        color: paletteColor,
                      }}
                    >
                      {Icon ? <Icon sx={{ fontSize: 18 }} /> : (
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 900 }}>
                          {meta.label?.[0] || 'L'}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: paletteColor, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {meta.label}
                      </Typography>
                      <Typography sx={{ mt: 0.22, fontSize: '0.79rem', lineHeight: 1.26, color: alpha(colors.semantic.ink, 0.72), display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {getEntryText(entry)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: alpha(colors.semantic.ink, 0.54), lineHeight: 1 }}>
                      {getEntryTimeLabel(entry)}
                    </Typography>
                  </Box>
                );
              })}
              {!visibleRightRailEntries.length ? (
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.35, color: alpha(colors.semantic.ink, 0.62) }}>
                  No matching logs for this day.
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default DesktopDashboardWorkspace;
